// nebula/glsl.js: shaders of the nebula world.
// Bake (once per look, slice by slice into 3D targets): BAKE_DETAIL, BAKE_MACRO, BAKE_LIGHT.
// Per-frame shaders live in march.js (MARCH, VIS) and comp.js (COMPOSITE, MOTES).
// POST_* is a small copy of the site post chain for snapshot().
import { GLSL_NOISE } from '../lib/common.js';

export const VERT = /* glsl */`varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;
// for passes that only use gl_FragCoord (no unread varying: Firefox warns about those)
export const VERT0 = /* glsl */`void main(){ gl_Position = vec4(position.xy, 0.0, 1.0); }`;

export const COMMON = /* glsl */`
float sq(float x) { return x * x; }
// smoothstep that also accepts reversed edges (GLSL leaves edge0 >= edge1 undefined)
float sst(float a, float b, float x) { float t = clamp((x - a) / (b - a), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }
// Henyey-Greenstein, normalised so isotropic = 1
float hg(float c, float g) { float g2 = g * g; return (1.0 - g2) / pow(max(1.0 + g2 - 2.0 * g * c, 1e-4), 1.5); }
vec2 boxHit(vec3 ro, vec3 rd, vec3 bmin, vec3 bmax) {
  vec3 inv = 1.0 / (rd + vec3(1e-7));
  vec3 t0 = (bmin - ro) * inv, t1 = (bmax - ro) * inv;
  vec3 tn = min(t0, t1), tf = max(t0, t1);
  return vec2(max(max(tn.x, tn.y), tn.z), min(min(tf.x, tf.y), tf.z));
}
vec3 hash33n(vec3 p3) { p3 = fract(p3 * vec3(0.1031, 0.1030, 0.0973)); p3 += dot(p3, p3.yxz + 33.33); return fract((p3.xxy + p3.yxx) * p3.zyx); }
`;

/* ---------------- bake: tiling detail noise (period 1) ---------------- */
export const BAKE_DETAIL = /* glsl */`
precision highp float;
uniform float uZ, uN;
vec3 h33(vec3 p) { p = fract(p * vec3(0.1031, 0.1030, 0.0973)); p += dot(p, p.yxz + 33.33); return fract((p.xxy + p.yxx) * p.zyx); }
vec3 gradAt(vec3 c, float per, float s) { return normalize(h33(mod(c, per) + s) - 0.5 + 1e-5); }
float gnoise(vec3 x, float per, float s) {
  vec3 i = floor(x), f = fract(x);
  vec3 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float a = dot(gradAt(i, per, s), f);
  float b = dot(gradAt(i + vec3(1, 0, 0), per, s), f - vec3(1, 0, 0));
  float c = dot(gradAt(i + vec3(0, 1, 0), per, s), f - vec3(0, 1, 0));
  float d = dot(gradAt(i + vec3(1, 1, 0), per, s), f - vec3(1, 1, 0));
  float e = dot(gradAt(i + vec3(0, 0, 1), per, s), f - vec3(0, 0, 1));
  float g = dot(gradAt(i + vec3(1, 0, 1), per, s), f - vec3(1, 0, 1));
  float h = dot(gradAt(i + vec3(0, 1, 1), per, s), f - vec3(0, 1, 1));
  float k = dot(gradAt(i + vec3(1, 1, 1), per, s), f - vec3(1, 1, 1));
  return mix(mix(mix(a, b, u.x), mix(c, d, u.x), u.y), mix(mix(e, g, u.x), mix(h, k, u.x), u.y), u.z);
}
float worley(vec3 x, float per, float s) {
  vec3 i = floor(x), f = fract(x);
  float md = 9.0;
  for (int z = -1; z <= 1; z++) for (int y = -1; y <= 1; y++) for (int w = -1; w <= 1; w++) {
    vec3 o = vec3(float(w), float(y), float(z));
    vec3 dd = o + h33(mod(i + o, per) + s + 7.7) - f;
    md = min(md, dot(dd, dd));
  }
  return clamp(sqrt(md), 0.0, 1.0);
}
void main() {
  vec3 x = vec3(gl_FragCoord.xy / uN, uZ);
  // R perlin-worley (billows), G worley fbm, B ridged fbm (filaments), A smooth fbm
  float pf = 0.0, amp = 0.5, fr = 4.0;
  for (int o = 0; o < 5; o++) { pf += amp * gnoise(x * fr, fr, 0.0); fr *= 2.0; amp *= 0.5; }
  pf = clamp(pf * 1.3 + 0.5, 0.0, 1.0);
  float wf = 1.0 - (0.625 * worley(x * 4.0, 4.0, 1.0) + 0.25 * worley(x * 8.0, 8.0, 2.0) + 0.125 * worley(x * 16.0, 16.0, 3.0));
  float pw = clamp((pf - (1.0 - wf)) / max(wf, 0.05) * 0.5 + pf * 0.5, 0.0, 1.0);
  float wg = 1.0 - (0.625 * worley(x * 8.0, 8.0, 4.0) + 0.25 * worley(x * 16.0, 16.0, 5.0) + 0.125 * worley(x * 32.0, 32.0, 6.0));
  float rf = 0.0; amp = 0.55; fr = 4.0;
  for (int o = 0; o < 4; o++) { float n = 1.0 - abs(gnoise(x * fr, fr, 9.0 + float(o)) * 1.9); rf += amp * n * n; fr *= 2.0; amp *= 0.5; }
  float sf = 0.0; amp = 0.5; fr = 2.0;
  for (int o = 0; o < 4; o++) { sf += amp * gnoise(x * fr, fr, 21.0 + float(o)); fr *= 2.0; amp *= 0.5; }
  sf = clamp(sf * 1.5 + 0.5, 0.0, 1.0);
  gl_FragColor = vec4(pw, clamp(wg, 0.0, 1.0), clamp(rf, 0.0, 1.0), sf);
}`;

/* ---------------- bake: macro structure of one look ---------------- */
export const BAKE_MACRO = /* glsl */`
precision highp float;
uniform float uZ; uniform vec3 uRes, uBoxMin, uBoxSize; uniform float uSeed;
uniform vec4 uWarp;                                  // amp, freq, amp2, freq2
uniform vec4 uWall; uniform vec3 uWallC, uWallOpen;  // on, R, noise, cover
uniform vec4 uWall2;                                 // holes, thickness
uniform vec4 uPB[6]; uniform vec4 uPT[6];            // pillar base.xyz + r1, tip.xyz + r2
uniform vec4 uGlob[8];                               // globule centre + radius
uniform vec4 uShell; uniform vec3 uShellC;           // on, R, thickness, fragmentation
uniform vec4 uLobe; uniform vec3 uLobeAx;            // on, length, width, waist
uniform vec4 uRing; uniform vec3 uRingN;             // on, R, tube, clumping
uniform vec4 uCliff;                                 // on, top y, amplitude, frequency
uniform vec4 uCliff2;                                // face z, face noise amp, face noise freq, -
uniform vec4 uLanes;                                 // amount, frequency, z near, z far
uniform vec4 uGas;                                   // amount, frequency, radius, filaments
uniform vec4 uFront;                                 // veil: amount, frequency, light-side bias, dust clearance
uniform vec3 uLDirM;
uniform vec3 uStar;
${GLSL_NOISE}
float sq(float x) { return x * x; }
float sst(float a, float b, float x) { float t = clamp((x - a) / (b - a), 0.0, 1.0); return t * t * (3.0 - 2.0 * t); }
float sdRoundCone(vec3 p, vec3 a, vec3 b, float r1, float r2) {
  vec3 ba = b - a; float l2 = dot(ba, ba); float rr = r1 - r2; float a2 = l2 - rr * rr; float il2 = 1.0 / l2;
  vec3 pa = p - a; float y = dot(pa, ba); float z = y - l2;
  vec3 xv = pa * l2 - ba * y; float x2 = dot(xv, xv); float y2 = y * y * l2; float z2 = z * z * l2;
  float k = sign(rr) * rr * rr * x2;
  if (sign(z) * a2 * z2 > k) return sqrt(x2 + z2) * il2 - r2;
  if (sign(y) * a2 * y2 < k) return sqrt(x2 + y2) * il2 - r1;
  return (sqrt(x2 * a2 * il2) + y * rr) * il2 - r1;
}
void main() {
  vec3 uvw = vec3(gl_FragCoord.xy / uRes.xy, uZ);
  vec3 p = uBoxMin + uvw * uBoxSize;
  vec3 s3 = vec3(uSeed * 1.37, uSeed * 2.11, uSeed * 0.73);
  vec3 w1 = vec3(fbm3(p * uWarp.y + s3, 3), fbm3(p * uWarp.y + s3 + 19.1, 3), fbm3(p * uWarp.y + s3 + 37.7, 3));
  vec3 q = p + w1 * uWarp.x;
  vec3 w2 = vec3(fbm3(q * uWarp.w + s3 + 5.3, 2), fbm3(q * uWarp.w + s3 + 23.9, 2), fbm3(q * uWarp.w + s3 + 41.2, 2));
  q += w2 * uWarp.z;
  float body = fbm3(q * 0.55 + s3 + 8.0, 4);
  float dust = 0.0;
  if (uWall.x > 0.5) {
    vec3 c = q - uWallC; float r = length(c); vec3 n = c / max(r, 1e-3);
    float R = uWall.y * (1.0 + uWall.z * fbm3(n * 1.6 + s3 + 3.0, 4));
    float inWall = smoothstep(-0.25, 0.25 + uWall2.y, r - R);
    float cover = smoothstep(uWall.w - 0.3, uWall.w + 0.35, -dot(n, uWallOpen));
    float holes = mix(1.0, smoothstep(-0.3, 0.35, body + 0.1), uWall2.x);
    dust = max(dust, inWall * cover * mix(0.06, 1.0, holes));
  }
  for (int i = 0; i < 6; i++) {
    if (uPB[i].w <= 0.0) continue;
    float sd = sdRoundCone(q, uPB[i].xyz, uPT[i].xyz, uPB[i].w, uPT[i].w);
    dust = max(dust, sst(0.08, -0.2, sd) * (0.85 + 0.3 * body));
  }
  for (int i = 0; i < 8; i++) {
    if (uGlob[i].w <= 0.0) continue;
    float r = length(q - uGlob[i].xyz) / uGlob[i].w;
    dust = max(dust, sst(1.15, 0.35, r));
  }
  if (uShell.x > 0.5) {
    vec3 c = q - uShellC; float r = length(c); vec3 n = c / max(r, 1e-3);
    float R = uShell.y * (1.0 + 0.16 * fbm3(n * 2.0 + s3, 3));
    float sh = exp(-sq((r - R) / uShell.z));
    float frag = smoothstep(-0.2, 0.35, fbm3(q * 1.2 + s3 + 2.0, 4) + 0.1);
    dust = max(dust, sh * mix(1.0, frag, uShell.w) * 0.95 * (1.0 - 0.8 * smoothstep(0.3, 0.85, n.z)));   // thin front cap (camera side): a limb-brightened bubble, the star shows through
  }
  if (uLobe.x > 0.5) {
    vec3 c = q - uStar; float h = dot(c, uLobeAx); float rc = length(c - uLobeAx * h);
    float ah = abs(h);
    float prof = uLobe.z * sqrt(max(0.0, 1.0 - sq(ah / uLobe.y))) * (0.3 + 0.7 * smoothstep(0.0, uLobe.y * 0.45, ah));
    float lobe = exp(-sq((rc - prof) / (0.18 + 0.14 * ah / uLobe.y))) * sst(uLobe.y * 1.05, uLobe.y * 0.7, ah);
    float waist = exp(-sq(h / 0.25)) * exp(-sq((rc - uLobe.w) / 0.4));
    dust = max(dust, max(lobe * (0.6 + 0.4 * body), waist));
  }
  float ringGas = 0.0;
  if (uRing.x > 0.5) {
    // M57 (JWST): a torus of long radial strands streaming from the star, outer knots, ionised gas.
    // Angle varies fast and radius slowly, so ridged noise becomes spokes (fine strands: the march).
    vec3 c = q - uStar; float h = dot(c, uRingN); float rc = length(c - uRingN * h);
    float rr = length(c); vec3 cr = c / max(rr, 1e-3);
    float R = uRing.y, W = uRing.z;
    float s1 = clamp(1.0 - abs(fbm3(cr * 4.5 + c * 0.15 + s3 + 5.0, 3)) * 2.2, 0.0, 1.0);
    float s2 = clamp(1.0 - abs(fbm3(cr * 8.0 + c * 0.25 + s3 + 9.0, 2)) * 2.4, 0.0, 1.0);
    float fil = s1 * s1 * (0.35 + 0.9 * s2);
    float cl = mix(1.0, smoothstep(-0.35, 0.35, fbm3(c * 1.6 + s3, 3)), uRing.w * 0.5);
    float shell = exp(-(sq((rc - R) / (W * 1.25)) + sq(h / (W * 1.7))));
    float outer = exp(-(sq((rc - R * 1.18) / (W * 0.8)) + sq(h / (W * 1.4))));
    float knots = smoothstep(0.45, 0.85, s1) * smoothstep(0.3, 0.8, s2) * outer;
    dust = max(dust, (shell * 0.12 * fil + knots * 0.4) * cl);
    ringGas = shell * (0.5 + 1.6 * fil) * cl + 0.3 * outer * fil;
  }
  float above = 9.0;
  if (uCliff.x > 0.5) {
    float top = uCliff.y + uCliff.z * (fbm3(vec3(q.x, 0.0, q.z) * uCliff.w + s3 + 11.0, 5) + 0.4 * fbm3(q * uCliff.w * 2.3, 3));
    float face = uCliff2.x + uCliff2.y * fbm3(vec3(q.x, q.y * 0.7, 0.0) * uCliff2.z + s3 + 17.0, 4);
    face += uCliff2.w * max(0.0, -1.0 - q.y) * (0.8 + 0.4 * body);   // phones: the cliff foot slopes toward the camera (fills the bottom band)
    float inFace = sst(0.15, -0.35, q.z - face);
    dust = max(dust, sst(0.12, -0.3, q.y - top) * inFace * (0.85 + 0.25 * body));
    above = max(q.y - top, (q.z - face) * 1.5);
    // steam: ridged streaks rising off the eroding ridge, stretched vertically
    float st = 1.0 - abs(fbm3(vec3(q.x * 1.3, q.y * 0.35 - 0.2 * q.x, q.z * 1.3) + s3 + 31.0, 4)) * 2.2;
    float steam = pow(max(st, 0.0), 3.0) * exp(-max(above, 0.0) / 1.1) * smoothstep(-0.2, 0.25, above);
    dust = max(dust, steam * 0.55);
  }
  if (uLanes.x > 0.0) {
    float low = smoothstep(1.5, -0.5, p.y);
    float zin = smoothstep(uLanes.z - 0.6, uLanes.z + 0.8, p.z) * sst(uLanes.w + 0.6, uLanes.w - 0.8, p.z);
    float clear = smoothstep(1.2, 3.8, length((p - uStar).xy));
    float ln = fbm3(q * uLanes.y + s3 + 51.0, 4);
    dust = max(dust, smoothstep(0.08, 0.42, ln) * zin * clear * low * uLanes.x);
  }
  vec3 cs = p - uStar; float rs = length(cs);
  float gas = uGas.x * (0.35 + 0.65 * smoothstep(-0.4, 0.5, fbm3(q * uGas.y + s3 + 61.0, 4)));
  gas *= smoothstep(0.2, 1.4, rs) * exp(-rs / uGas.z);
  float fil = fbm3(q * uGas.y * 2.3 + s3 + 71.0, 3);
  gas += uGas.w * uGas.x * pow(max(0.0, 1.0 - abs(fil) * 3.0), 4.0) * exp(-rs / (uGas.z * 1.5));
  gas += 0.35 * dust * uGas.x;
  gas += ringGas * uGas.x * 2.6;
  vec3 e = min(uvw, 1.0 - uvw); e.y = 1.0 - uvw.y;
  float edge = smoothstep(0.0, 0.06, min(e.x, min(e.y, e.z)));
  float chem = clamp(0.5 + 0.9 * fbm3(p * 0.22 + s3 + 40.0, 3), 0.0, 1.0);
  // ionised veil: wispy diffuse gas filling the cavities, thicker toward the ionising cluster
  float vn = fbm3(q * uFront.y + s3 + 81.0, 4);
  float vw = pow(max(0.0, 1.0 - abs(fbm3(q * uFront.y * 1.9 + s3 + 91.0, 3)) * 2.4), 3.0);
  float veil = uFront.x * (0.12 * smoothstep(0.0, 0.6, vn) + 1.1 * vw * smoothstep(-0.25, 0.35, vn));
  veil *= smoothstep(-7.0, 5.0, dot(p, uLDirM) * uFront.z) * (1.0 - smoothstep(0.1, uFront.w, dust));
  veil *= 0.1 + 1.3 * exp(-max(above, 0.0) / 1.1);
  gl_FragColor = vec4(sqrt(clamp(dust * edge, 0.0, 1.0)), sqrt(clamp(gas * edge, 0.0, 1.0)), chem, sqrt(clamp(veil * edge, 0.0, 1.0)));
}`;

/* ---------------- bake: transmittance to the star / the cluster + ionisation-front skins ---------------- */
// R: sqrt(T to the star)  G: sqrt(T to the cluster)  B: cluster-facing skin  A: sqrt(T to the diffuse fill).
// The star's birth pocket (radius uClr.xy) is excluded: after ignition the cocoon is gone.
export const BAKE_LIGHT = /* glsl */`
precision highp float; precision highp sampler3D;
uniform sampler3D uSrc; uniform float uZ; uniform vec3 uRes, uBoxMin, uBoxMax, uBoxSize;
uniform vec3 uStar, uLDir, uLFill; uniform float uExt, uExtL, uStep; uniform vec4 uClr;
${COMMON}
float dAt(vec3 p) {
  vec3 uvw = (p - uBoxMin) / uBoxSize;
  if (any(lessThan(uvw, vec3(0.0))) || any(greaterThan(uvw, vec3(1.0)))) return 0.0;
  float s = texture(uSrc, uvw).r;
  return s * s;
}
float dAtS(vec3 p) { return dAt(p) * smoothstep(uClr.x, uClr.y, length(p - uStar)); }
// optical depth with a FIXED step and a fractional last sample: sample positions vary
// continuously with p (ceil(L/step) steps would re-space them per voxel -> shell contours)
float odS(vec3 p, vec3 dir, float L, float h0) {
  h0 = max(h0, L / 159.0);
  float nf = L / h0, n = floor(nf), f = nf - n, od = 0.0;
  for (int i = 0; i < 160; i++) { if (float(i) >= n) break; od += dAtS(p + dir * (float(i) + 0.5) * h0); }
  return (od + f * dAtS(p + dir * (n + 0.5 * f) * h0)) * h0;
}
float odL(vec3 p, vec3 dir, float L, float h0) {
  h0 = max(h0, L / 99.0);
  float nf = L / h0, n = floor(nf), f = nf - n, od = 0.0;
  for (int i = 0; i < 100; i++) { if (float(i) >= n) break; od += dAt(p + dir * (float(i) + 0.5) * h0); }
  return (od + f * dAt(p + dir * (n + 0.5 * f) * h0)) * h0;
}
void main() {
  vec3 uvw = vec3(gl_FragCoord.xy / uRes.xy, uZ);
  vec3 p = uBoxMin + uvw * uBoxSize;
  vec3 dv = uStar - p; float L = length(dv); vec3 dir = dv / max(L, 1e-4);
  float Ts = exp(-uExt * odS(p, dir, L, uStep));
  vec2 tb = boxHit(p, uLDir, uBoxMin, uBoxMax);
  float L2 = clamp(tb.y, 0.0, 30.0);
  float Tl = exp(-uExtL * odL(p, uLDir, L2, uStep * 1.5));
  vec2 tf = boxHit(p, uLFill, uBoxMin, uBoxMax);
  float L3 = clamp(tf.y, 0.0, 30.0);
  float Tf = exp(-uExtL * 0.6 * odL(p, uLFill, L3, uStep * 2.5));
  float d0 = dAt(p);
  float aL = max(dAt(p - uLDir * 0.2), dAt(p - uLDir * 0.42) * 0.8);
  float skinL = Tl * smoothstep(0.03, 0.35, aL) * (1.0 - 0.6 * smoothstep(0.2, 0.8, d0));
  gl_FragColor = vec4(sqrt(Ts), sqrt(Tl), skinL, sqrt(Tf));
}`;

/* ---------------- low-res volume denoise: [1 2 1]^2 tent via 4 bilinear taps ---------------- */
export const DENOISE = /* glsl */`
precision highp float;
uniform sampler2D uSrc; uniform vec2 uTexel;
varying vec2 vUv;
void main() {
  // exact 3x3 box from 4 bilinear taps (weights 4/2/2/1): a 3x3 box is what cancels the
  // interleaved-gradient-noise step jitter (a tent left a halftone lattice); the full-res
  // edge pass in the composite restores the silhouettes this softens
  vec2 o = uTexel;
  vec4 c = texture2D(uSrc, vUv + o * vec2(-0.5, -0.5)) * 4.0 + texture2D(uSrc, vUv + o * vec2(1.0, -0.5)) * 2.0
         + texture2D(uSrc, vUv + o * vec2(-0.5, 1.0)) * 2.0 + texture2D(uSrc, vUv + o * vec2(1.0, 1.0));
  gl_FragColor = c / 9.0;
}`;

/* ---------------- poster post chain (snapshot only; mirrors web/src/post.js) ---------------- */
export const POST_DOWN = /* glsl */`
uniform sampler2D src; uniform vec2 texel; uniform float first, threshold;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(src, vUv).rgb * 4.0;
  c += texture2D(src, vUv + texel * vec2(-1.0, -1.0)).rgb;
  c += texture2D(src, vUv + texel * vec2(1.0, -1.0)).rgb;
  c += texture2D(src, vUv + texel * vec2(-1.0, 1.0)).rgb;
  c += texture2D(src, vUv + texel * vec2(1.0, 1.0)).rgb;
  c /= 8.0;
  if (first > 0.5) {
    float br = max(c.r, max(c.g, c.b));
    float knee = threshold * 0.6;
    float soft = clamp(br - threshold + knee, 0.0, 2.0 * knee);
    soft = soft * soft / (4.0 * knee + 1e-5);
    c *= max(soft, br - threshold) / max(br, 1e-5);
    c = min(c, vec3(60.0));
  }
  gl_FragColor = vec4(c, 1.0);
}`;
export const POST_UP = /* glsl */`
uniform sampler2D src, base; uniform vec2 texel;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(src, vUv + texel * vec2(-2.0, 0.0)).rgb + texture2D(src, vUv + texel * vec2(2.0, 0.0)).rgb;
  c += texture2D(src, vUv + texel * vec2(0.0, -2.0)).rgb + texture2D(src, vUv + texel * vec2(0.0, 2.0)).rgb;
  c += (texture2D(src, vUv + texel * vec2(-1.0, -1.0)).rgb + texture2D(src, vUv + texel * vec2(1.0, -1.0)).rgb) * 2.0;
  c += (texture2D(src, vUv + texel * vec2(-1.0, 1.0)).rgb + texture2D(src, vUv + texel * vec2(1.0, 1.0)).rgb) * 2.0;
  gl_FragColor = vec4(c / 12.0 + texture2D(base, vUv).rgb, 1.0);
}`;
export const POST_FINAL = /* glsl */`
uniform sampler2D scene, bloom;
uniform float bloomK, exposure, vignette, sat, contrast;
uniform vec3 shadows, highs, gainC;
uniform vec2 res;
varying vec2 vUv;
float hash(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
vec3 aces(vec3 x) { return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0); }
vec3 toSRGB(vec3 c) { return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c)); }
void main() {
  vec3 c = texture2D(scene, vUv).rgb + texture2D(bloom, vUv).rgb * bloomK;
  c *= exposure * gainC;
  c = aces(c);
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c += shadows * (1.0 - smoothstep(0.0, 0.25, l)) * 0.035;
  c += highs * smoothstep(0.25, 1.0, l) * 0.12;
  c = max(mix(vec3(l), c, sat), 0.0);
  c = toSRGB(c);
  c = (c - 0.5) * contrast + 0.5;
  vec2 d = vUv - 0.5;
  float v = length(d * vec2(res.x / res.y, 1.0) * 0.9);
  c *= 1.0 - vignette * smoothstep(0.35, 1.05, v);
  c += (hash(gl_FragCoord.xy + 91.7) + hash(gl_FragCoord.xy * 1.61 + 3.3) - 1.0) / 255.0;
  gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
}`;
